// @flow

import React, { Component } from "react";
import _ from "lodash";

import { Redirect } from "react-router-dom";
import { withStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Button from "@material-ui/core/Button";
import AddIcon from "@material-ui/icons/Add";
import Card from "@material-ui/core/Card";
import Snackbar from "@material-ui/core/Snackbar";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import DeleteIcon from "@material-ui/icons/Delete";

import type { UserProfile } from "../types/user_profile";
import type {
  SyncObject,
  SyncAction,
  CustomFieldDefinition,
  ValidationError
} from "../types/sync_object";
import SaveIcon from "@material-ui/icons/Save";
import SyncObjectApi from "../lib/sync_object_api";
import Action from "../lib/action";

const styles = theme => ({
  root: {
    paddingBottom: theme.spacing.unit * 10
  },
  headline: {
    marginTop: theme.spacing.unit * 4,
    marginLeft: theme.spacing.unit,
    marginRight: theme.spacing.unit
  },
  subheading: {
    marginTop: theme.spacing.unit * 2,
    marginLeft: theme.spacing.unit,
    marginRight: theme.spacing.unit
  },
  textField: {
    marginLeft: theme.spacing.unit,
    marginRight: theme.spacing.unit,
    width: 300
  },
  textArea: {
    "& textarea": {
      fontFamily: "'Roboto Mono', monospace",
      fontSize: "80%"
    },
    marginLeft: theme.spacing.unit * 2,
    marginRight: theme.spacing.unit * 2,
    width: 600
  },
  switch: {
    marginLeft: theme.spacing.unit,
    marginRight: theme.spacing.unit
  },
  fab: {
    height: "40px",
    width: "40px",
    marginLeft: theme.spacing.unit * 2,
    marginRight: theme.spacing.unit * 2
  },
  fabSubHeading: {
    height: "40px",
    width: "40px",
    marginLeft: theme.spacing.unit,
    marginRight: theme.spacing.unit
  },
  actionCard: {
    width: 275,
    marginLeft: theme.spacing.unit,
    marginRight: theme.spacing.unit * 4,
    marginTop: theme.spacing.unit * 4
  },
  saveButton: {
    margin: theme.spacing.unit,
    marginTop: theme.spacing.unit * 4,
    paddingRight: theme.spacing.unit * 2,
    paddingLeft: theme.spacing.unit * 2,
    width: "60px"
  },
  saveIcon: {
    marginRight: theme.spacing.unit,
    fontSize: 20
  },
  saveSuccessClose: {
    width: theme.spacing.unit * 4,
    height: theme.spacing.unit * 4
  },
  customDefinitionDelete: {
    justifyContent: "right"
  }
});

type Props = {
  classes: any,
  setTitle: string => void,
  showBackButton: () => void,
  hideBackButton: () => void,
  user: UserProfile,
  objectName: ?string
};

type State = {
  inProgress: boolean,
  syncObject: ?SyncObject,
  redirectTo: ?any,
  showSaveSuccessDialog: boolean,
  onSaveTriggered: boolean
};

class BaseSyncObjectForm extends Component<Props, State> {
  state = {
    redirectTo: null,
    inProgress: true,
    syncObject: null,
    showSaveSuccessDialog: false,
    onSaveTriggered: false
  };

  isCreateView = (): boolean => {
    return !this.props.objectName;
  };

  isEditView = (): boolean => {
    return !this.isCreateView();
  };

  handleClick = () => {
    this.setState({ showSaveSuccessDialog: true });
  };

  closeSaveSuccessDialog = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    this.setState({ showSaveSuccessDialog: false });
  };

  editableSyncObject = (syncObject: Object): SyncObject => {
    const protected_inbound_attribute_names =
      syncObject.protected_inbound_attribute_names || [];
    const synced_attribute_fields = syncObject.synced_attribute_fields || [];
    const inbound_mapping = syncObject.inbound_mapping
      ? JSON.stringify(syncObject.inbound_mapping, null, 4)
      : "";
    const outbound_mapping = syncObject.outbound_mapping
      ? JSON.stringify(syncObject.outbound_mapping, null, 4)
      : "";

    const actions = syncObject.actions.map(action => {
      const queried_attribute_names = action.queried_attribute_names || [];
      return {
        ...action,
        queried_attribute_names: queried_attribute_names.join(","),
        out_of_band_query_count: action.out_of_band_query_count || 0,
        out_of_band_position: action.out_of_band_position || 0,
        out_of_band_collection_method_name:
          action.out_of_band_collection_method_name || "",
        state_valid_for_action_method:
          action.state_valid_for_action_method || ""
      };
    });

    return {
      ...syncObject,
      syncs_status: syncObject.syncs_status || false,
      nilifying_delete: syncObject.nilifying_delete || false,
      unsynced_warning_collection_method_name:
        syncObject.unsynced_warning_collection_method_name || "",
      inbound_mapping: inbound_mapping,
      outbound_mapping: outbound_mapping,
      protected_inbound_attribute_names: protected_inbound_attribute_names.join(
        ", "
      ),
      synced_attribute_fields: synced_attribute_fields.join(", "),
      custom_field_definitions: syncObject.custom_field_definitions || [],
      actions: actions
    };
  };

  storableSyncObject = (): Object => {
    if (!this.state.syncObject) {
      throw new Error("Illegal method invocation");
    }

    const syncObject = this.state.syncObject;
    const inbound_mapping = syncObject.inbound_mapping
      ? JSON.parse(syncObject.inbound_mapping)
      : null;
    const outbound_mapping = syncObject.outbound_mapping
      ? JSON.parse(syncObject.outbound_mapping)
      : null;
    const unsynced_warning_collection_method_name =
      syncObject.unsynced_warning_collection_method_name || null;

    const actions = syncObject.actions.map(action => {
      return {
        ...action,
        queried_attribute_names: action.queried_attribute_names
          .split(",")
          .map(el => el.trim())
          .filter(el => el)
      };
    });

    return {
      ...syncObject,
      inbound_mapping: inbound_mapping,
      outbound_mapping: outbound_mapping,
      unsynced_warning_collection_method_name: unsynced_warning_collection_method_name,
      protected_inbound_attribute_names: syncObject.protected_inbound_attribute_names
        .split(",")
        .map(el => el.trim())
        .filter(el => el),
      synced_attribute_fields: syncObject.synced_attribute_fields
        .split(",")
        .map(el => el.trim())
        .filter(el => el),
      actions: actions
    };
  };

  newEmptySyncObject = () => {
    return {
      object_name: "",
      syncs_status: false,
      class_name: "",
      maximum_failure_count: 0,
      out_of_band_position: 0,
      nilifying_delete: false,
      unsynced_warning_collection_method_name: "",
      actions: [this.newEmptyAction()],
      inbound_mapping: "",
      outbound_mapping: "",
      protected_inbound_attribute_names: "",
      synced_attribute_fields: "",
      custom_field_definitions: [],
      validationErrors: []
    };
  };

  componentDidMount = async () => {
    if (this.isEditView()) {
      /* this.setState({
       *   inProgress: false,
       *   syncObject: this.editableSyncObject(appymt)
       * });
       */
      const idToken = await this.props.user.getIdToken(true);
      const response = await SyncObjectApi.get(this.props.objectName, idToken);
      this.setState({
        inProgress: false,
        syncObject: this.editableSyncObject(response)
      });
      if (this.state.syncObject) {
        this.props.setTitle(`Editing ${this.state.syncObject.class_name}`);
      }
    } else {
      this.setState({
        inProgress: false,
        syncObject: this.newEmptySyncObject()
      });
    }
  };

  onSave = async () => {
    this.setState({ onSaveTriggered: true });
    const syncObject = this.storableSyncObject();
    const idToken = await this.props.user.getIdToken(true);
    await SyncObjectApi.createOrUpdate(syncObject, idToken);
    this.setState({ showSaveSuccessDialog: true, onSaveTriggered: false });
  };

  fieldHasError = (
    errorHolder: ?{ validationErrors: ?(ValidationError[]) },
    fieldName: string
  ): boolean => {
    return this.errorForField(errorHolder, fieldName) ? true : false;
  };

  errorForField = (
    errorHolder: ?{ validationErrors: ?(ValidationError[]) },
    fieldName: string
  ): ?string => {
    if (!errorHolder || !errorHolder.validationErrors) {
      return null;
    }

    let error = errorHolder.validationErrors.find(
      el => el.fieldName === fieldName
    );

    return error ? error.error : null;
  };

  anyFieldHasError = (): boolean => {
    const syncObject = this.state.syncObject;
    if (!syncObject) {
      return false;
    }

    let hasBaseErrors: boolean =
      !!syncObject.validationErrors && syncObject.validationErrors.length !== 0;
    let hasActionsErrors: boolean = syncObject.actions.some(
      (action: SyncAction) =>
        action.validationErrors && action.validationErrors.length !== 0
    );
    let hasCustomFieldDefinitionErrors: boolean = syncObject.custom_field_definitions.some(
      (cfd: CustomFieldDefinition) =>
        cfd.validationErrors && cfd.validationErrors.length !== 0
    );

    return hasBaseErrors || hasActionsErrors || hasCustomFieldDefinitionErrors;
  };

  handleFieldChange = (fieldName: string, val: mixed, error: ?string) => {
    this.setState(state => {
      if (!state.syncObject) {
        return state;
      }

      let validationErrors;
      if (state.syncObject.validationErrors) {
        validationErrors = [...state.syncObject.validationErrors];
        const oldError = validationErrors.find(
          el => el.fieldName === fieldName
        );

        if (oldError) {
          _.remove(validationErrors, el => el.fieldName === fieldName);
        }
      } else {
        validationErrors = [];
      }

      if (error) {
        validationErrors.push({ fieldName, error });
      }

      const newState: State = {
        ...state,
        syncObject: {
          ...state.syncObject,
          [fieldName]: val,
          validationErrors
        }
      };
      return newState;
    });
  };

  handleArrayFieldChange = (fieldName: string, required: boolean = false) => (
    event: SyntheticInputEvent<HTMLInputElement>
  ) => {
    let val = event.target.value;
    let error = null;

    if (
      (required && !val) ||
      (val &&
        val
          .split(",")
          .map(el => el.trim())
          .filter(el => el).length === 0)
    ) {
      error = "Invalid value";
    }
    this.handleFieldChange(fieldName, val, error);
  };

  handleJSONFieldChange = (fieldName: string) => (
    event: SyntheticInputEvent<HTMLInputElement>
  ) => {
    const val = event.target.value;
    let error = null;
    if (val) {
      try {
        JSON.parse(val);
      } catch (err) {
        if (err.name === "SyntaxError") {
          error = "Invalid value";
        }
      }
    }

    this.handleFieldChange(fieldName, val, error);
  };

  handleNumberFieldChange = (fieldName: string, required: boolean = false) => (
    event: SyntheticInputEvent<HTMLInputElement>
  ) => {
    let val = event.target.value;
    let error = null;

    const parsedVal = parseInt(val, 10);
    if ((required && !val) || (val && isNaN(parsedVal))) {
      error = "Enter a valid non-zero number";
    }

    this.handleFieldChange(fieldName, val, error);
  };

  handleStringFieldChange = (fieldName: string, required: boolean = false) => (
    event: SyntheticInputEvent<HTMLInputElement>
  ) => {
    const val = event.target.value;
    let error = null;

    if (required && !val) {
      error = "Cannot be blank";
    }

    this.handleFieldChange(fieldName, val, error);
  };

  handleBooleanFieldChange = (fieldName: string) => (
    event: SyntheticInputEvent<HTMLInputElement>
  ) => {
    const val: boolean = event.target.checked;
    this.handleFieldChange(fieldName, val);
  };

  /*
     This method is to handle changes to fields on objects which are nested in arrays
     For eg, `action` is such an object in an array, which has the following structure
     sync_object = {
         actions: [
              {
                  <field-change-to-handle>
              }
         ]
     }

   */
  handleNestedArrayFieldChange = (
    arrayFieldName: string,
    arrayIndex: number,
    fieldName: string,
    val: mixed,
    error: ?string
  ) =>
    this.setState((state: State) => {
      const syncObject = state.syncObject;

      if (!syncObject) {
        return;
      }

      const arr = syncObject[arrayFieldName];
      const item = arr[arrayIndex];

      let validationErrors: ValidationError[];
      if (item.validationErrors) {
        validationErrors = [...item.validationErrors];
        const oldError = validationErrors.find(
          el => el.fieldName === fieldName
        );

        if (oldError) {
          _.remove(validationErrors, el => el.fieldName === fieldName);
        }
      } else {
        validationErrors = [];
      }

      if (error) {
        validationErrors.push({
          fieldName,
          error
        });
      }

      const newItem = {
        ...item,
        [fieldName]: val,
        validationErrors
      };
      const newArray = Object.assign([], arr, { [arrayIndex]: newItem });

      let newState = {
        ...state,
        syncObject: {
          ...syncObject,
          [arrayFieldName]: newArray
        }
      };

      return newState;
    });

  handleBooleanArrayFieldChange = (arrayFieldName, arrayIndex, fieldName) => (
    event: SyntheticInputEvent<HTMLInputElement>
  ) => {
    const val: boolean = event.target.checked;
    this.handleNestedArrayFieldChange(
      arrayFieldName,
      arrayIndex,
      fieldName,
      val,
      null
    );
  };

  handleDeepArrayFieldChange = (
    arrayFieldName: string,
    arrayIndex: number,
    fieldName: string,
    required: boolean = false
  ) => (event: SyntheticInputEvent<HTMLInputElement>) => {
    let val = event.target.value;
    let error = null;

    if (
      (required && !val) ||
      (val &&
        val
          .split(",")
          .map(el => el.trim())
          .filter(el => el).length === 0)
    ) {
      error = "Invalid value";
    }
    this.handleNestedArrayFieldChange(
      arrayFieldName,
      arrayIndex,
      fieldName,
      val,
      error
    );
  };

  handleStringArrayFieldChange = (
    arrayFieldName,
    arrayIndex,
    fieldName,
    required: boolean = false
  ) => (event: SyntheticInputEvent<HTMLInputElement>) => {
    const val: string = event.target.value;
    let error = null;

    if (required && !val) {
      error = "Cannot be blank";
    }

    this.handleNestedArrayFieldChange(
      arrayFieldName,
      arrayIndex,
      fieldName,
      val,
      error
    );
  };

  handleNumberArrayFieldChange = (
    arrayFieldName,
    arrayIndex,
    fieldName,
    required: boolean = false
  ) => (event: SyntheticInputEvent<HTMLInputElement>) => {
    const val = event.target.value;
    let error = null;

    const parsedVal = parseInt(val, 10);
    if ((required && !val) || (val && isNaN(parsedVal))) {
      error = "Enter a valid non-zero number";
    }
    this.handleNestedArrayFieldChange(
      arrayFieldName,
      arrayIndex,
      fieldName,
      val,
      error
    );
  };

  handleSaveSuccessClose = (event, reason) => {
    if (reason !== "clickaway") {
      this.setState({ showSaveSuccessDialog: false });
    }
  };

  newEmptyAction = (): SyncAction => {
    return {
      name: "",
      active: false,
      direction: "",
      job_class_name: "",
      out_of_band_query_count: 0,
      out_of_band: false,
      supports_batch: false,
      out_of_band_position: 0,
      out_of_band_collection_method_name: "",
      state_valid_for_action_method: "",
      queried_attribute_names: "",
      validationErrors: [
        {
          fieldName: "name",
          error: "Cannot be blank"
        },
        {
          fieldName: "direction",
          error: "Cannot be blank"
        },
        {
          fieldName: "job_class_name",
          error: "Cannot be blank"
        }
      ]
    };
  };

  addNewAction = () => {
    if (!this.state.syncObject) {
      return;
    }

    const actions = this.state.syncObject.actions;
    if (actions.length > 0) {
      const lastAction = actions[actions.length - 1];
      if (
        !lastAction.name &&
        !lastAction.direction &&
        !lastAction.job_class_name &&
        !lastAction.out_of_band_query_count
      ) {
        return;
      }
    }

    this.setState({
      syncObject: {
        ...this.state.syncObject,
        actions: [...actions, this.newEmptyAction()]
      }
    });
  };

  deleteAction = (index: number) => () => {
    if (!this.state.syncObject) {
      return;
    }

    let actions = [...this.state.syncObject.actions];
    actions.splice(index, 1);

    if (actions.length === 0) {
      actions.push(this.newEmptyAction());
    }

    this.setState({
      syncObject: {
        ...this.state.syncObject,
        actions: actions
      }
    });
  };

  renderActions = (actions: SyncAction[], classes) => {
    return actions.map((action, index) => {
      return (
        <Action
          key={index}
          classes={classes}
          action={action}
          index={index}
          handleStringArrayFieldChange={this.handleStringArrayFieldChange}
          handleNumberArrayFieldChange={this.handleNumberArrayFieldChange}
          handleBooleanArrayFieldChange={this.handleBooleanArrayFieldChange}
          handleDeepArrayFieldChange={this.handleDeepArrayFieldChange}
          fieldHasError={this.fieldHasError}
          errorForField={this.errorForField}
          deleteAction={this.deleteAction}
        />
      );
    });
  };

  newEmptyCustomFieldDefinition = () => {
    return {
      field_id: "",
      length: 0,
      validationErrors: [
        {
          fieldName: "field_id",
          error: "Cannot be blank"
        },
        {
          fieldName: "length",
          error: "Enter a valid non-zero number"
        }
      ]
    };
  };

  addNewCustomFieldDefinition = () => {
    if (!this.state.syncObject) {
      return;
    }

    const customFieldDefinitions = this.state.syncObject
      .custom_field_definitions;
    if (customFieldDefinitions.length > 0) {
      const lastCustomFieldDefinition =
        customFieldDefinitions[customFieldDefinitions.length - 1];
      if (
        !lastCustomFieldDefinition.field_id &&
        !lastCustomFieldDefinition.length
      ) {
        return;
      }
    }

    this.setState({
      syncObject: {
        ...this.state.syncObject,
        custom_field_definitions: [
          ...customFieldDefinitions,
          this.newEmptyCustomFieldDefinition()
        ]
      }
    });
  };

  deleteCustomFieldDefinition = (index: number) => () => {
    if (!this.state.syncObject) {
      return;
    }

    let customFieldDefinitions = [
      ...this.state.syncObject.custom_field_definitions
    ];
    customFieldDefinitions.splice(index, 1);

    /* Do not append a new item if list is empty, because this field is non-required
     * if (customFieldDefinitions.length === 0) {
     *   customFieldDefinitions.push(this.newEmptyCustomFieldDefinition());
     * }
     */

    this.setState({
      syncObject: {
        ...this.state.syncObject,
        custom_field_definitions: customFieldDefinitions
      }
    });
  };

  renderCustomFieldDefinitions = (
    customFieldDefinitions: CustomFieldDefinition[],
    classes
  ) =>
    customFieldDefinitions.map((customFieldDefinition, index) => (
      <Grid item key={index}>
        <Card className={classes.actionCard}>
          <Grid container>
            <TextField
              label="Field Name"
              onChange={this.handleStringArrayFieldChange(
                "custom_field_definitions",
                index,
                "field_id"
              )}
              margin="normal"
              className={classes.textField}
              value={customFieldDefinition.field_id}
              error={this.fieldHasError(customFieldDefinition, "field_id")}
              helperText={this.errorForField(customFieldDefinition, "field_id")}
            />
            <TextField
              label="Length"
              onChange={this.handleNumberArrayFieldChange(
                "custom_field_definitions",
                index,
                "length"
              )}
              margin="normal"
              className={classes.textField}
              value={customFieldDefinition.length}
              error={this.fieldHasError(customFieldDefinition, "length")}
              helperText={this.errorForField(customFieldDefinition, "length")}
            />
          </Grid>
          <Grid container className={classes.customDefinitionDelete}>
            <Grid item xs={2}>
              <IconButton
                aria-label="Delete"
                onClick={this.deleteCustomFieldDefinition(index)}
              >
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
        </Card>
      </Grid>
    ));

  render = () => {
    const { classes } = this.props;
    if (this.state.redirectTo) {
      return (
        <Redirect to={`/objects/${this.state.redirectTo.editObjectName}`} />
      );
    } else if (this.state.inProgress || !this.state.syncObject) {
      return <CircularProgress className="progress-indicator" size={50} />;
    }

    const syncObject = this.state.syncObject;

    return (
      <Grid container className={classes.root}>
        <Grid container>
          <Grid item>
            <Typography
              className={classes.headline}
              variant="headline"
              gutterBottom
            >
              Basic
            </Typography>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item>
            <TextField
              label="Object Name"
              margin="normal"
              className={classes.textField}
              disabled={this.isEditView()}
              value={syncObject.object_name}
              onChange={this.handleStringFieldChange("object_name", true)}
            />
          </Grid>
          <Grid item>
            <TextField
              label="Class Name"
              margin="normal"
              className={classes.textField}
              onChange={this.handleStringFieldChange("class_name", true)}
              value={syncObject.class_name}
              error={this.fieldHasError(this.state.syncObject, "class_name")}
              helperText={this.errorForField(
                this.state.syncObject,
                "class_name"
              )}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item>
            <TextField
              label="Max. Failure count"
              onChange={this.handleNumberFieldChange(
                "maximum_failure_count",
                true
              )}
              margin="normal"
              className={classes.textField}
              value={syncObject.maximum_failure_count}
              error={this.fieldHasError(
                this.state.syncObject,
                "maximum_failure_count"
              )}
              helperText={this.errorForField(
                this.state.syncObject,
                "maximum_failure_count"
              )}
            />
          </Grid>
          <Grid item>
            <TextField
              label="Out of Band position"
              onChange={this.handleNumberFieldChange(
                "out_of_band_position",
                true
              )}
              margin="normal"
              className={classes.textField}
              value={syncObject.out_of_band_position}
              error={this.fieldHasError(
                this.state.syncObject,
                "out_of_band_position"
              )}
              helperText={this.errorForField(
                this.state.syncObject,
                "out_of_band_position"
              )}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item>
            <TextField
              label="Unsynced Warning Collection Method Name"
              onChange={this.handleStringFieldChange(
                "unsynced_warning_collection_method_name"
              )}
              margin="normal"
              className={classes.textField}
              value={syncObject.unsynced_warning_collection_method_name}
              error={this.fieldHasError(
                this.state.syncObject,
                "unsynced_warning_collection_method_name"
              )}
              helperText={this.errorForField(
                this.state.syncObject,
                "unsynced_warning_collection_method_name"
              )}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item>
            <FormControlLabel
              className={classes.switch}
              control={
                <Switch
                  checked={syncObject.syncs_status}
                  onChange={this.handleBooleanFieldChange("syncs_status")}
                  color="primary"
                />
              }
              label="Sync enabled?"
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              className={classes.switch}
              control={
                <Switch
                  checked={syncObject.nilifying_delete}
                  onChange={this.handleBooleanFieldChange("nilifying_delete")}
                  color="primary"
                />
              }
              label="Nilifying delete?"
            />
          </Grid>
        </Grid>
        <Grid container className={classes.headline}>
          <Grid item>
            <Typography variant="headline" gutterBottom>
              Actions
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant="fab"
              className={classes.fab}
              color="primary"
              onClick={this.addNewAction}
            >
              <AddIcon />
            </Button>
          </Grid>
        </Grid>
        <Grid container>{this.renderActions(syncObject.actions, classes)}</Grid>
        <Grid container className={classes.headline}>
          <Grid item>
            <Typography variant="headline" gutterBottom>
              Mappings
            </Typography>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item>
            <TextField
              label="Protected inbound attribute names"
              helperText="Use comma-separated values"
              onChange={this.handleArrayFieldChange(
                "protected_inbound_attribute_names",
                true
              )}
              margin="normal"
              className={classes.textField}
              value={syncObject.protected_inbound_attribute_names}
              error={this.fieldHasError(
                this.state.syncObject,
                "protected_inbound_attribute_names"
              )}
            />
          </Grid>
          <Grid item>
            <TextField
              label="Synced attribute fields"
              helperText="Use comma-separated values"
              onChange={this.handleArrayFieldChange(
                "synced_attribute_fields",
                true
              )}
              margin="normal"
              className={classes.textField}
              value={syncObject.synced_attribute_fields}
              error={this.fieldHasError(
                this.state.syncObject,
                "synced_attribute_fields"
              )}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item>
            <TextField
              label="Inbound Mapping"
              helperText="Enter valid JSON here"
              multiline
              rows="15"
              margin="normal"
              className={classes.textArea}
              onChange={this.handleJSONFieldChange("inbound_mapping")}
              value={syncObject.inbound_mapping}
              error={this.fieldHasError(
                this.state.syncObject,
                "inbound_mapping"
              )}
            />
          </Grid>
          <Grid item>
            <TextField
              label="Outbound Mapping"
              helperText="Enter valid JSON here"
              multiline
              rows="15"
              margin="normal"
              className={classes.textArea}
              onChange={this.handleJSONFieldChange("outbound_mapping")}
              value={syncObject.outbound_mapping}
              error={this.fieldHasError(
                this.state.syncObject,
                "outbound_mapping"
              )}
            />
          </Grid>
        </Grid>
        <Grid container className={classes.subheading}>
          <Grid item>
            <Typography variant="subheading" gutterBottom>
              Custom field definitions
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant="fab"
              className={classes.fabSubHeading}
              color="primary"
              onClick={this.addNewCustomFieldDefinition}
            >
              <AddIcon />
            </Button>
          </Grid>
        </Grid>
        <Grid container>
          {this.renderCustomFieldDefinitions(
            syncObject.custom_field_definitions,
            classes
          )}
        </Grid>
        <div>
          <Snackbar
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left"
            }}
            open={this.state.showSaveSuccessDialog}
            autoHideDuration={6000}
            onClose={this.closeSaveSuccessDialog}
            ContentProps={{
              "aria-describedby": "message-id"
            }}
            message={<span id="message-id">Saved successfully</span>}
            action={[
              <IconButton
                key="close"
                aria-label="Close"
                color="inherit"
                className={classes.saveSuccessClose}
                onClick={this.closeSaveSuccessDialog}
              >
                <CloseIcon />
              </IconButton>
            ]}
          />
        </div>
        <Grid container>
          <Grid item>
            <Button
              className={classes.saveButton}
              variant="raised"
              size="large"
              disabled={this.anyFieldHasError() || this.state.onSaveTriggered}
              color="primary"
              onClick={this.onSave}
            >
              <SaveIcon className={classes.saveIcon} />
              Save
            </Button>
          </Grid>
        </Grid>
      </Grid>
    );
  };
}

export default withStyles(styles)(BaseSyncObjectForm);



// WEBPACK FOOTER //
// ./src/lib/BaseSyncObjectForm.js